import { useNavigationItems } from '@src/hooks/useNavigationItems';
import type { AVA_ENVS } from '@src/utils/envConfig';
import { useTheme as useHelixTheme } from '@talos/helix';
import type { ThemeTypes } from '@talos/kyoko';
import {
  abbreviateId,
  Box,
  ENV_COLORS,
  Flex,
  FormControlSizes,
  Icon,
  IconName,
  IndicatorBadge,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  Stack,
  ThemeToggle,
  Toggle,
  useAuthContext,
  useMixpanel,
} from '@talos/kyoko';
import { startTransition, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import { useBackendVersion, useFeatureFlag, useUser } from '../../hooks';
import { useMobileLayoutState } from '../../hooks/useMobileLayoutState';
import { useThemeTypePreference } from '../../hooks/useThemeTypePreference';
import { useDisplaySettings } from '../../providers/DisplaySettingsProvider';
import type { NavigationItem } from '../../tokens/navigationPaths';
import { appVersion, isNonReleaseVersion } from '../../utils/version';
import { SidebarItem } from './SidebarItem';
import { PopoverMenu, PopoverMenuItem } from './styles';

const POPOVER_MENU_ITEM_HEIGHT = 32;
const POPOVER_VERSION_HEIGHT = 62;
const LOGOUT_AND_LABELS = 2;
interface SidebarUserProfileItemProps {
  env: AVA_ENVS;
  uiVersion: string;
}

export function SidebarUserProfileItem({ env, uiVersion }: SidebarUserProfileItemProps) {
  const { userMenuItems } = useNavigationItems();
  const user = useUser();
  const auth = useAuthContext();
  const mixpanel = useMixpanel();
  const { showSidebarLabels, setShowSidebarLabels } = useDisplaySettings();
  const { themeType, setThemeType } = useThemeTypePreference();
  const { isDesktopLayoutForcedForSession, setIsMobileLayoutDisabledForSession } = useMobileLayoutState();
  const { setType: setHelixThemeType } = useHelixTheme();
  const handleThemeChange = useCallback(
    (themeType: ThemeTypes) => {
      setThemeType(themeType);
      setHelixThemeType(themeType);
      mixpanel.track(MixpanelEvent.ChangeThemeType, { [MixpanelEventProperty.Type]: themeType });
    },
    [setThemeType, setHelixThemeType, mixpanel]
  );
  const handleSidebarLabelsChange = useCallback(
    (checked: boolean) => {
      setShowSidebarLabels(checked);
      mixpanel.track(MixpanelEvent.ToggleSidebarLabels, {
        [MixpanelEventProperty.Enabled]: checked,
        [MixpanelEventProperty.Source]: MixpanelEventSource.NavigationSidebar,
      });
    },
    [setShowSidebarLabels, mixpanel]
  );
  const { enableThemeTypeSelection } = useFeatureFlag();
  const menuItemCount =
    LOGOUT_AND_LABELS +
    userMenuItems.length +
    (isDesktopLayoutForcedForSession ? 1 : 0) +
    (enableThemeTypeSelection ? 1 : 0);

  const { backendVersion, versionRef } = useBackendVersion();
  const isProdEnv = env === 'prod';
  // Strip RC suffixes in production
  const displayVersion = appVersion(uiVersion, isProdEnv);
  // When version is 0.0.0 (non-release build), show abbreviated git hash instead
  const gitHash = import.meta.env.VITE_GIT_HASH;
  const uiVersionLabel =
    isNonReleaseVersion(uiVersion) && gitHash ? `UI #${abbreviateId(gitHash)}` : `UI v${displayVersion}`;
  const versionParts = [backendVersion, uiVersionLabel].filter(Boolean);
  const envPrefix = isProdEnv ? '' : `${env.toUpperCase()} `;
  const versionText = `${envPrefix}${versionParts.join(' / ')}`;

  const popoverMenuHeight = POPOVER_MENU_ITEM_HEIGHT * menuItemCount + POPOVER_VERSION_HEIGHT + 8;

  return (
    <SidebarItem
      data-testid="sidebar-user-profile-item"
      icon={IconName.UserCircle}
      menuMaxHeight={popoverMenuHeight}
      highlight
      menuItems={
        <PopoverMenu h={popoverMenuHeight}>
          <Stack
            ref={versionRef}
            m="spacing4"
            h={POPOVER_VERSION_HEIGHT}
            background="backgroundCard"
            borderRadius="borderRadiusDefault"
            gap="spacing8"
            py="spacing4"
            px="spacing8"
            data-testid="sidebar-user-profile-version"
          >
            <Stack justifyContent="flex-start">
              <Flex mb="spacing2" gap="spacing4" color="colorTextImportant">
                {user?.Name}
                <IndicatorBadge>{user?.Organization}</IndicatorBadge>
              </Flex>
              <Box color="colorTextDefault" fontSize="fontSizeXs">
                {user?.Email}
              </Box>
            </Stack>
            <Flex
              justifyContent="flex-start"
              lineHeight="16px"
              color={ENV_COLORS[env ?? ''] ?? 'gray.090'}
              fontWeight="fontWeightMedium"
              data-testid="sidebar-user-profile-version-text"
            >
              {versionText}
            </Flex>
          </Stack>
          {userMenuItems.map((item: NavigationItem) =>
            item.path ? (
              <PopoverMenuItem key={item.path} as={NavLink} to={item.path}>
                {item.icon && <Icon icon={item.icon} />} {item.label}
              </PopoverMenuItem>
            ) : null
          )}
          {isDesktopLayoutForcedForSession && (
            <PopoverMenuItem
              as="button"
              data-testid="use-mobile-version-button"
              onClick={() => {
                mixpanel.track(MixpanelEvent.ReenableMobileUI);
                startTransition(() => setIsMobileLayoutDisabledForSession(false));
              }}
            >
              <Icon icon={IconName.DeviceMobile} /> Use mobile version
            </PopoverMenuItem>
          )}

          <PopoverMenuItem as="div">
            <Icon icon={IconName.InformationCircle} />
            <Box mr="auto">Sidebar Labels</Box>
            <Toggle size={FormControlSizes.Small} checked={showSidebarLabels} onChange={handleSidebarLabelsChange} />
          </PopoverMenuItem>
          {enableThemeTypeSelection && (
            <PopoverMenuItem as="div">
              <Icon icon={IconName.ColorSwatch} /> Theme
              <ThemeToggle
                ml="auto"
                themeType={themeType}
                onChangeThemeType={handleThemeChange}
                size={FormControlSizes.Tiny}
              />
            </PopoverMenuItem>
          )}
          <PopoverMenuItem
            onClick={() => {
              setIsMobileLayoutDisabledForSession(false);
              mixpanel.track(MixpanelEvent.LogOut);
              auth.logout();
            }}
            borderTop={true}
            data-testid="logout-button"
          >
            <Icon icon={IconName.Logout} /> Logout
          </PopoverMenuItem>
        </PopoverMenu>
      }
    />
  );
}
